import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Play } from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";
import { AnimatedCard } from "./AnimatedCard";

export const ServicesSection = () => {
  const isMobile = useIsMobile();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoUrl = "https://drive.google.com/file/d/1_Y7_oN7Je0rEUAjv4W89gwoclOvZP4zZ/preview";

  return (
    <>
      {/* Sobre Nós - bg-gray-50 */}
      <section id="sobre" className="py-10 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-blue-600 mb-8 sm:mb-12">
            Sobre Nós
          </h2>

          {/* Desktop: Video left, Cards right | Mobile: Cards above, Video below */}
          <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-12">
            {/* Video Section */}
            <AnimatedSection className="w-full lg:w-1/2 lg:sticky lg:top-24 lg:self-center flex justify-center">
              <div className="relative w-full max-w-[700px] mx-auto">
                <div className="relative w-full h-[280px] sm:h-[350px] lg:h-0 lg:pt-[56.25%]">
                  {!isVideoPlaying ? (
                    <button
                      onClick={() => setIsVideoPlaying(true)}
                      className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg overflow-hidden group cursor-pointer"
                    >
                      <img
                        src="/images/video-thumbnail.jpg"
                        alt="Video thumbnail"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity duration-300 group-hover:bg-black/40">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                          <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="white" />
                        </div>
                      </div>
                    </button>
                  ) : (
                    <iframe
                      src={videoUrl}
                      allow="autoplay"
                      className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                      allowFullScreen
                    />
                  )}
                </div>
              </div>
            </AnimatedSection>

            {/* Cards Section */}
            <div className="w-full lg:w-1/2 space-y-4">
              <AnimatedCard alignment="left" delay={100}>
                <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <h4 className="text-xl font-semibold text-blue-600 mb-3">Nossa Missão</h4>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Fornecer soluções técnicas e consultoria especializada para otimizar o uso dos recursos hídricos, 
                    considerando os aspectos técnicos, ambientais e sociais relacionados.
                  </p>
                </div>
              </AnimatedCard>

              <AnimatedCard alignment="right" delay={200}>
                <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <h4 className="text-xl font-semibold text-blue-600 mb-3">Nossa Visão</h4>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Ser a empresa de engenharia referência em serviços de análise de informações hídricas, 
                    auxiliando organizações a tomarem decisões informadas e sustentáveis sobre o uso da água.
                  </p>
                </div>
              </AnimatedCard>

              <AnimatedCard alignment="left" delay={300}>
                <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  <h4 className="text-xl font-semibold text-blue-600 mb-3">Nossos Valores</h4>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Tecnologia, inovação, eficiência, sustentabilidade e compromisso com a sociedade.
                  </p>
                </div>
              </AnimatedCard>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Serviços - bg-white */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection delay={100}>
            <h3 className="text-3xl font-bold text-blue-600 mb-4">Nossos Serviços</h3>
            <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
          </AnimatedSection>
          <div className="space-y-6">
            <AnimatedCard alignment="left" delay={150}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Gestão de Recursos Hídricos</h4>
                <p className="text-gray-700">
                  Análise de dados de monitoramento, estudos hidrológicos e apoio à tomada de decisão.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="right" delay={250}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Engenharia Ambiental</h4>
                <p className="text-gray-700">
                  Estudos e implantação de programas ambientais, análise de qualidade da água e efluentes.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="left" delay={350}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Capacitação Profissional</h4>
                <p className="text-gray-700">
                  Treinamentos e workshops para o mercado de águas e energia.
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Nosso Diferencial - bg-gray-50 */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection delay={100}>
            <h3 className="text-3xl font-bold text-blue-600 mb-4">Nosso Diferencial</h3>
            <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
          </AnimatedSection>
          <div className="space-y-6">
            <AnimatedCard alignment="left" delay={150}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Tecnologia e Inovação</h4>
                <p className="text-gray-700">
                  Utilizamos inteligência artificial, aprendizado de máquina, computação em nuvem e ciência 
                  de dados para obter informações hídricas confiáveis e em tempo hábil.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="right" delay={250}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Expertise em Recursos Hídricos</h4>
                <p className="text-gray-700">
                  Nossa equipe multidisciplinar possui amplo conhecimento em engenharia ambiental, hidrologia 
                  e química, gerando soluções criativas e eficazes.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="left" delay={350}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Foco no Cliente</h4>
                <p className="text-gray-700">
                  Nosso objetivo é auxiliar organizações a tomarem decisões informadas e sustentáveis sobre o 
                  uso da água, promovendo a eficiência e a preservação dos recursos hídricos.
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Impacto da Informação Hídrica - bg-white */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection delay={100}>
            <h3 className="text-3xl font-bold text-blue-600 mb-4">Impacto da Informação Hídrica</h3>
            <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
          </AnimatedSection>
          <div className="space-y-6">
            <AnimatedCard alignment="left" delay={150}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Segurança de Ativos</h4>
                <p className="text-gray-700">
                  Informações hídricas confiáveis garantem a segurança e a integridade dos seus ativos de 
                  geração de energia.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="right" delay={250}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Eficiência Operacional</h4>
                <p className="text-gray-700">
                  Otimize o uso da água e reduza custos operacionais com base em dados precisos e em tempo real.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="left" delay={350}>
              <div className="bg-gray-50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Tomada de Decisão Assertiva</h4>
                <p className="text-gray-700">
                  Tome decisões informadas sobre o uso dos recursos hídricos, promovendo a sustentabilidade 
                  do seu negócio.
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Nossos Clientes - bg-gray-50 */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection delay={100}>
            <h3 className="text-3xl font-bold text-blue-600 mb-4">Nossos Clientes</h3>
            <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
          </AnimatedSection>
          <div className="space-y-6">
            <AnimatedCard alignment="left" delay={150}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Setor Elétrico</h4>
                <p className="text-gray-700">
                  Soluções para geração de energia hidrelétrica.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="right" delay={250}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Saneamento Básico</h4>
                <p className="text-gray-700">
                  Serviços de análise e gestão de recursos hídricos.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="left" delay={350}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Indústria</h4>
                <p className="text-gray-700">
                  Consultoria em química e tratamento de efluentes.
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard alignment="right" delay={450}>
              <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <h4 className="text-xl font-semibold text-blue-600 mb-4">Meio Ambiente</h4>
                <p className="text-gray-700">
                  Estudos e programas ambientais em bacias hidrográficas.
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>
    </>
  );
};
